import { useEffect, useState } from 'react';

import { fetchWaveform, type WaveformData } from '../audio/waveform-data';

/** How often to re-check while a bake is still in flight. */
const POLL_MS = 8_000;

type LoadedWaveform = {
  trackId: string;
  data: WaveformData | null;
  pending: boolean;
};

/**
 * Load a track's baked waveform, polling while it is still being analysed.
 *
 * A track becomes playable as soon as its metadata lands, so `null` here is a normal
 * transient state rather than an error — callers draw a flat placeholder and the real
 * shape arrives when the bake finishes.
 *
 * The loaded track id is kept *in* the state rather than being cleared by the effect.
 * That way switching tracks is a derivation during render instead of a setState that
 * would trigger a second render pass before the new fetch even starts.
 */
export function useWaveform(trackId: string | null, expected: boolean): {
  data: WaveformData | null;
  pending: boolean;
} {
  const [loaded, setLoaded] = useState<LoadedWaveform | null>(null);

  useEffect(() => {
    if (!trackId) return;

    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const load = async () => {
      try {
        const result = await fetchWaveform(trackId, controller.signal);
        if (cancelled) return;
        setLoaded({ trackId, data: result, pending: result === null });
        if (result === null) timer = setTimeout(() => void load(), POLL_MS);
      } catch {
        if (cancelled) return;
        // A failed fetch is not worth surfacing: the waveform is decoration over
        // playback that already works.
        setLoaded({ trackId, data: null, pending: false });
      }
    };

    void load();

    return () => {
      cancelled = true;
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [trackId]);

  // Anything loaded for a different track is not ours to show.
  const current = loaded && loaded.trackId === trackId ? loaded : null;
  return {
    data: current?.data ?? null,
    pending: current ? current.pending : Boolean(trackId) && expected,
  };
}
